// theme.ts
import {
  createTheme,
  responsiveFontSizes,
  alpha,
  darken,
  decomposeColor,
  getContrastRatio,
  getLuminance,
  hslToRgb,
  lighten,
  recomposeColor,
  rgbToHex,
} from "@mui/material/styles";
import type { Theme } from "@mui/material/styles";

declare module "@mui/material/styles" {
  interface TypeBackground {
    /**
     * Superfície levemente destacada do `paper` — cabeçalhos de tabela, linhas
     * zebradas, balões de chat. Derivada de `paper`/`text`, então acompanha o
     * modo claro/escuro (ao contrário de `grey[50]`, que é sempre claro).
     */
    subtle: string;
  }
}

/**
 * Cores de marca do cliente. Espelha o que o app mobile chama de
 * primary/secondary/background/text no endpoint `/api/clients/:id/theme`.
 *
 * - `primary`    → cor de destaque (botões, links, ícones)
 * - `secondary`  → cor da "superfície" da aplicação (fundo das páginas)
 * - `background` → fundo dos cards/papéis
 * - `text`       → cor principal do texto
 */
export type BrandColors = {
  primary: string;
  secondary: string;
  background: string;
  text: string;
};

/** Já na forma normalizada por `toHex`, para `sanitizeBrandColors` ser idempotente. */
export const defaultBrandColors: BrandColors = {
  primary: "#1b6b4a",
  secondary: "#f0e9db",
  background: "#ffffff",
  text: "#3d3a34",
};

/** Contraste mínimo do WCAG AA para texto normal. */
export const TEXT_CONTRAST = 4.5;
/** Contraste mínimo do WCAG AA para ícones e bordas (1.4.11). */
const UI_CONTRAST = 3;

/**
 * Normaliza qualquer cor suportada para `#rrggbb`. Vários `sx` da aplicação
 * concatenam alpha no formato `${cor}15`, o que exige exatamente 6 dígitos —
 * por isso o canal alpha é descartado e hex curto (`#abc`) é expandido.
 * `rgbToHex` sozinho não serve: ele devolve a string intacta quando já começa
 * com `#` e emite 8 dígitos para entradas com alpha.
 */
const toHex = (color: string) => {
  const { values } = decomposeColor(color.startsWith("hsl") ? hslToRgb(color) : color);
  return rgbToHex(`rgb(${values[0]}, ${values[1]}, ${values[2]})`);
};

/** Interpola duas cores (0 = totalmente `from`, 1 = totalmente `to`). */
export const mix = (from: string, to: string, amount: number) => {
  // `toHex` garante dois triplos RGB sem alpha, então os canais casam 1:1.
  const a = decomposeColor(toHex(from));
  const b = decomposeColor(toHex(to));
  const values = a.values.map((v, i) =>
    Math.round(v + (b.values[i]! - v) * amount),
  ) as typeof a.values;

  return toHex(recomposeColor({ ...a, values }));
};

const readableOn = (background: string) =>
  getContrastRatio(background, "#ffffff") >= TEXT_CONTRAST ? "#ffffff" : "#1a1a1a";

/**
 * Aproxima `color` de preto ou branco (o que estiver mais longe de
 * `background`) até atingir o contraste pedido. Preserva o matiz da marca em
 * vez de trocar a cor por preto/branco puro. Se por esse lado não chega (uma
 * cor escura sobre um fundo escuro pouco mais claro que ela), tenta o outro.
 */
export const ensureContrast = (color: string, background: string, ratio: number) => {
  if (getContrastRatio(color, background) >= ratio) return toHex(color);

  const darkerFirst = getLuminance(background) > getLuminance(color);
  for (const goDarker of [darkerFirst, !darkerFirst]) {
    for (let step = 1; step <= 20; step++) {
      const candidate = goDarker ? darken(color, step * 0.05) : lighten(color, step * 0.05);
      if (getContrastRatio(candidate, background) >= ratio) return toHex(candidate);
    }
  }

  return readableOn(background);
};

const isValidColor = (color: string | undefined | null): color is string => {
  if (!color) return false;
  try {
    decomposeColor(color.startsWith("hsl") ? hslToRgb(color) : color);
    return true;
  } catch {
    return false;
  }
};

/** Descarta cores inválidas vindas da API, caindo no padrão davinTI. */
export const sanitizeBrandColors = (brand?: Partial<BrandColors> | null): BrandColors => ({
  primary: isValidColor(brand?.primary) ? toHex(brand.primary) : defaultBrandColors.primary,
  secondary: isValidColor(brand?.secondary) ? toHex(brand.secondary) : defaultBrandColors.secondary,
  background: isValidColor(brand?.background) ? toHex(brand.background) : defaultBrandColors.background,
  text: isValidColor(brand?.text) ? toHex(brand.text) : defaultBrandColors.text,
});

/**
 * Monta o tema MUI a partir das cores do cliente. Tudo que não vem da API
 * (tons de cinza, divisórias, textos secundários) é derivado das quatro cores
 * base, então qualquer paleta cadastrada continua coerente.
 *
 * O web opera sempre em modo claro — só a linha `light` do tema do cliente é
 * consultada. A linha `dark` do back existe para o app mobile.
 */
export const createAppTheme = (brandColors?: Partial<BrandColors> | null): Theme => {
  const brand = sanitizeBrandColors(brandColors);
  const { primary, secondary: surface, background: paper } = brand;

  // O texto aparece tanto sobre `paper` (cards) quanto sobre `surface` (fundo
  // das páginas), que podem ter luminâncias bem diferentes — a guarda usa o
  // pior dos dois.
  const worstBg =
    getContrastRatio(brand.text, surface) < getContrastRatio(brand.text, paper) ? surface : paper;

  // O tenant edita essas cores numa tela própria; sem esta guarda uma
  // combinação ruim (texto claro sobre papel claro) deixa o painel ilegível —
  // e as cores ficam em cache, então nem um refresh recupera.
  const text = ensureContrast(brand.text, worstBg, TEXT_CONTRAST);

  const shade = (amount: number) => toHex(darken(primary, amount));
  const tint = (amount: number) => toHex(lighten(primary, amount));

  // `secondary` entra como cor de frente (ícones e bordas dos cards de módulo),
  // então precisa passar no 3:1 do WCAG 1.4.11 contra o papel.
  const secondaryMain = ensureContrast(tint(0.35), paper, UI_CONTRAST);

  // A rampa do MUI vai de 50 (mais clara) a 900 (mais escura) — componentes
  // internos contam com isso. As pontas são ordenadas por luminância em vez de
  // assumir que `surface` é sempre a mais clara: um tenant pode cadastrar cores
  // escuras na linha light, e a rampa não pode inverter por causa disso.
  const [rampFrom, rampTo] =
    getLuminance(surface) >= getLuminance(text)
      ? ([surface, text] as const)
      : ([text, surface] as const);
  const ramp = (amount: number) => mix(rampFrom, rampTo, amount);

  let theme = createTheme({
    palette: {
      mode: "light",

      primary: {
        main: primary,
        light: tint(0.55),
        dark: shade(0.3),
        contrastText: readableOn(primary),
      },

      secondary: {
        main: secondaryMain,
        light: tint(0.7),
        dark: shade(0.2),
        contrastText: readableOn(secondaryMain),
      },

      success: {
        main: "#16a34a",
      },

      warning: {
        main: "#c47a18",
      },

      error: {
        main: "#b52e2e",
      },

      background: {
        default: surface,
        paper,
        subtle: mix(paper, text, 0.08),
      },

      text: {
        primary: text,
        secondary: ensureContrast(mix(text, surface, 0.3), worstBg, TEXT_CONTRAST),
      },

      grey: {
        50: toHex(rampFrom),
        100: ramp(0.04),
        200: ramp(0.1),
        300: ramp(0.15),
        400: ramp(0.35),
        500: ramp(0.55),
        600: ramp(0.7),
        700: ramp(0.85),
        800: toHex(rampTo),
        900: mix(rampTo, "#000000", 0.2),
      },

      divider: mix(surface, text, 0.15),
    },

    typography: {
      fontFamily: `"Inter", "Roboto", "Helvetica", "Arial", sans-serif`,

      h1: {
        fontWeight: 700,
        letterSpacing: "-0.5px",
      },
      h2: {
        fontWeight: 700,
      },
      h3: {
        fontWeight: 600,
      },
      h4: {
        fontWeight: 600,
      },
      h5: {
        fontWeight: 600,
      },
      h6: {
        fontWeight: 600,
      },

      button: {
        textTransform: "none",
        fontWeight: 600,
      },
    },

    shape: {
      borderRadius: 6,
    },

    components: {
      MuiCssBaseline: {
        styleOverrides: (theme) => ({
          body: {
            backgroundColor: theme.palette.background.default,
            scrollbarWidth: 'thin',
            scrollbarColor: `${theme.palette.grey[400]} transparent`,
          },
          '*::-webkit-scrollbar': {
            width: '10px',
          },
          '*::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '*::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.primary.main,
            borderRadius: '10px',
            border: '2px solid transparent',
            backgroundClip: 'content-box',
            '&:hover': {
              backgroundColor: theme.palette.grey[500],
            },
          },
        }),
      },

      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            padding: "8px 20px",
          },
          containedPrimary: {
            boxShadow: `0 4px 14px ${alpha(primary, 0.25)}`,
          },
        },
      },

      MuiCard: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: 8,
            border: `1px solid ${theme.palette.divider}`,
          }),
        },
      },

      MuiAppBar: {
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            boxShadow: "0px 1px 3px rgba(0,0,0,0.06)",
          }),
        },
      },

      MuiTextField: {
        defaultProps: {
          variant: "outlined",
        },
      },

      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 8,
          },
        },
      },

      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
          },
        },
      },
    },
  });

  theme = responsiveFontSizes(theme);

  return theme;
};

/** Tema padrão davinTI, usado enquanto nenhum cliente estiver selecionado. */
const theme = createAppTheme();

export default theme;
